import { isEmpty } from 'lodash-es';
import {
  forwardRef,
  type PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { IntlProvider, type MessageFormatElement } from 'react-intl';

import 'wicg-inert';
import { getLanguage, getTimezone } from '@dynatrace-sdk/user-preferences';

import * as styles from './AppRoot.sty.js';
import { useCurrentTheme } from '../../hooks/useCurrentTheme.js';
import { useVisualRefreshEnabled } from '../../hooks/useVisualRefreshEnabled.js';
import { FocusProvider } from '../../providers/FocusProvider.js';
import { type DataTestId } from '../../types/data-props.js';
import { type MaskingProps } from '../../types/masking-props.js';
import { type StylingProps } from '../../types/styling-props.js';
import { initModalityDetection } from '../../utils/modality.js';

initModalityDetection();

const COMPONENT_NAME = 'AppRoot';

/**
 * Gets the href value set in the <base> element to prefix fetches correctly.
 * Will always end with a `/` value
 */
function getBaseHref(): string {
  const baseElement = document.querySelector('base');
  const href = baseElement?.href ?? '/';
  return href.endsWith('/') ? href : `${href}/`;
}

/**
 * AppRoot component props.
 * @public
 */
export type AppRootProps = PropsWithChildren<
  DataTestId & StylingProps & MaskingProps
> & {
  /**
   * The path to the folder of translations. Needs to always end with a `/` value and contain language files like en.json + ja.json.
   * The default path depends on your host domain and looks something like 'https://example.com/ui/lang/'.
   */
  translationRootPath?: string;
};

const defaultLanguage = 'en';

/**
 * In order to have all the providers in place for rendering overlays, applying
 * global styles or internationalization, you need to wrap your app in the
 * `AppRoot`. If you're using the `dt-app` to create your app, this is
 * automatically taken care of and you can start working on your app without
 * further ado.
 * @public
 */
export const AppRoot = /* @__PURE__ */ forwardRef<HTMLDivElement, AppRootProps>(
  (props, forwardedRef) => {
    const {
      style: consumerStyle,
      className: consumerClassName,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      translationRootPath = `${getBaseHref()}lang/`,
    } = props;

    /** Messages that are used for the translation. */
    const [messages, setMessages] = useState<
      Record<string, string> | Record<string, MessageFormatElement[]>
    >({});

    const { children } = props;

    const theme = useCurrentTheme();
    const language = getLanguage();
    let timezone = getTimezone();
    try {
      new Intl.DateTimeFormat(language, { timeZone: timezone });
    } catch {
      // If the passed timezone is not valid, we can fall back to the browsers
      // configured one.
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    /**
     * Synchronize the [data-theme] attribute to the root of the document
     * to have the design tokens defined in the cascade.
     */
    useLayoutEffect(() => {
      if (document !== undefined) {
        document.documentElement.setAttribute('data-theme', theme);
      }
    }, [theme]);

    const isVisualRefreshEnabled = useVisualRefreshEnabled();

    /**
     * Synchronize the [visualRefresh] attribute to the root of the document
     * to have the visual refresh toggle defined on the root.
     */
    useLayoutEffect(() => {
      if (document !== undefined && isVisualRefreshEnabled) {
        document.documentElement.classList.add(styles.dtVisualRefreshEnabled);
      }
    }, []);

    useEffect(() => {
      if (language === 'en') {
        // Don't fetch translations for 'en' to avoid an additional render cycle.
        // The default messages are in english already.
        return;
      }

      fetch(`${translationRootPath}${language}.json`)
        .then((res) => {
          return res.json();
        })
        .then((receivedMessages) => {
          setMessages(receivedMessages);
        })
        .catch(() => {
          // Only set messages to a new empty object if the previous messages were not empty.
          // `setMessages({})` would make react believe that this is a new object and, hence, re-render.
          // By just returning `prev` if the messages were already empty, we can avoid the additional render.
          setMessages((prev) => (isEmpty(prev) ? prev : {}));
        });
    }, [language]);

    return (
      <div
        ref={forwardedRef}
        data-testid={dataTestId}
        className={consumerClassName}
        style={consumerStyle}
        data-theme={theme}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        data-dt-component={COMPONENT_NAME}
      >
        <IntlProvider
          locale={language}
          timeZone={timezone}
          messages={messages}
          defaultLocale={defaultLanguage}
        >
          <FocusProvider>{children}</FocusProvider>
        </IntlProvider>
      </div>
    );
  },
);

(AppRoot as typeof AppRoot & { displayName: string }).displayName =
  COMPONENT_NAME;
